import { useState, useEffect } from 'react';
import { useApi } from './useApi.js';

/**
 * 列表通用 hook：分頁 + 排序(欄位,方向) + 任意篩選 + 防抖搜尋。前台後台共用。
 *
 * fetchFn 接 ({ page, limit, sort, q, ...filters }) → 回 PageResponse 形狀
 *   { items, total, page, totalPages }
 *
 * 用法：
 *   const list = usePagedList(p => adminGetThemes(p), { limit: 20, initialSort: 'displayOrder,asc' });
 *   list.items / list.total / list.totalPages
 *   list.page / list.setPage
 *   list.sort / list.toggleSort('name')   // 同欄位 asc→desc→清除
 *   list.q / list.setQ                     // 搜尋(自動防抖)
 *   list.filters / list.setFilter('type', 'd3')
 */
export function usePagedList(fetchFn, { limit = 20, initialSort = '', initialFilters = {} } = {}) {
  const [page, setPage] = useState(1);
  const [sort, setSortRaw] = useState(initialSort);
  const [filters, setFilters] = useState(initialFilters);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState(''); // 防抖後的搜尋字

  useEffect(() => {
    const h = setTimeout(() => { setDq(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(h);
  }, [q]);

  // 點欄位標題：不同欄位→asc；同欄位 asc→desc→清除(回預設)
  const toggleSort = (field) => {
    setPage(1);
    setSortRaw(prev => {
      const [f, d] = (prev || '').split(',');
      if (f !== field) return `${field},asc`;
      if (d === 'asc') return `${field},desc`;
      return '';
    });
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fkey = JSON.stringify(filters);
  const { data, loading, error, refetch } = useApi(
    () => fetchFn({ page, limit, sort: sort || undefined, q: dq || undefined, ...filters }),
    [page, sort, dq, fkey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    page, setPage,
    sort, toggleSort, setSort: setSortRaw,
    filters, setFilter,
    q, setQ,
    loading, error, refetch,
  };
}
